import React from 'react';
import styled from 'styled-components';
import { PACKAGES, packageRules, isIntegratedInverter } from './Promotions';

/**
 * The top of the Design page.
 *
 * Replaces the looping background video, which filled the screen and told the
 * customer nothing. In its place: photographs of the hardware they have actually
 * selected, what that package is, what it produces, and what it costs — followed
 * by a strip that names the one change standing between them and a promotion.
 *
 * The roof render is deliberately absent. It belongs to the pricing popup, one
 * step later, and showing a bare satellite photo here read as a finished design.
 */

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString('en-US')}`;

/* --------------------------------------------------------------- hero shell */

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  border: 1px solid #e0e6ea;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 12px;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Collage = styled.div`
  background: #f6fafd;
  border-right: 1px solid #e0e6ea;
  padding: 20px;
  display: flex;
  align-items: stretch;
  gap: 10px;

  @media screen and (max-width: 900px) {
    border-right: 0;
    border-bottom: 1px solid #e0e6ea;
  }
`;

const Piece = styled.div`
  flex: ${({ $wide }) => ($wide ? 1.25 : 1)};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Shot = styled.div`
  flex: 1;
  background: #fff;
  border: 1px solid #eaf0f4;
  border-radius: 6px;
  min-height: 108px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;

  img {
    max-width: 100%;
    max-height: 124px;
    width: auto;
    height: auto;
    display: block;
  }
`;

const Caption = styled.div`
  font-size: 0.62rem;
  line-height: 1.4;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #8098a8;

  b {
    display: block;
    color: #3c4e5a;
    letter-spacing: 0.07em;
  }
`;

/* ------------------------------------------------------------ package panel */

const Panel = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const Eyebrow = styled.div`
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #0b7fbd;
  font-weight: 700;
`;

const PackageName = styled.h2`
  font-size: 1.55rem;
  line-height: 1.1;
  color: #0f1b24;
  margin: 8px 0 0;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #41545f;
  margin: 8px 0 0;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid #eaf0f4;
  margin-top: 18px;

  @media screen and (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  padding: 11px 8px 11px 0;
  border-bottom: 1px solid #eaf0f4;
`;

const StatValue = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.1;
  color: #0f1b24;
  font-variant-numeric: tabular-nums;

  small {
    font-size: 0.62rem;
    font-weight: 700;
  }
`;

const StatKey = styled.div`
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8098a8;
  font-weight: 700;
  margin-top: 4px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 18px;
`;

const PriceNow = styled.div`
  font-size: 2.05rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #0f1b24;
  font-variant-numeric: tabular-nums;
`;

const PriceWas = styled.div`
  font-size: 0.85rem;
  color: #8098a8;
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
  padding-bottom: 3px;
`;

const Monthly = styled.div`
  margin-left: auto;
  text-align: right;

  b {
    display: block;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.15;
    color: #0f1b24;
    font-variant-numeric: tabular-nums;
  }

  span {
    font-size: 0.65rem;
    color: #8098a8;
  }
`;

const PromoFlag = styled.div`
  align-self: flex-start;
  margin-top: 12px;
  padding: 6px 11px;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${({ $earned }) => ($earned ? '#ffa300' : 'transparent')};
  color: ${({ $earned }) => ($earned ? '#20160a' : '#8098a8')};
  border: 1px solid ${({ $earned }) => ($earned ? '#ffa300' : '#e0e6ea')};
`;

/* --------------------------------------------------------------- switcher */

const Switcher = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid #e0e6ea;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;

  @media screen and (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SwitchButton = styled.button`
  appearance: none;
  background: ${({ $active }) => ($active ? '#eaf6fe' : '#fff')};
  border: 0;
  border-right: 1px solid #eaf0f4;
  border-top: 3px solid ${({ $active }) => ($active ? '#1BA8F1' : 'transparent')};
  text-align: left;
  padding: 14px 16px;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: background 160ms ease;

  &:last-child {
    border-right: 0;
  }

  &:hover {
    background: ${({ $active }) => ($active ? '#eaf6fe' : '#f6fafd')};
  }

  &:focus-visible {
    outline: 2px solid #1ba8f1;
    outline-offset: -3px;
  }

  @media screen and (max-width: 820px) {
    border-right: 0;
    border-bottom: 1px solid #eaf0f4;
  }
`;

const SwitchPromo = styled.span`
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $none }) => ($none ? '#8098a8' : '#b06a00')};
`;

const SwitchName = styled.span`
  font-size: 1rem;
  font-weight: 800;
  color: #0f1b24;
`;

const SwitchSubtitle = styled.span`
  font-size: 0.76rem;
  line-height: 1.4;
  color: #8098a8;
  min-height: 2.1em;
`;

const SwitchFigure = styled.span`
  font-size: 0.78rem;
  color: #8098a8;
  font-variant-numeric: tabular-nums;

  b {
    color: #3c4e5a;
  }
`;

/* ------------------------------------------------------------- eligibility */

const Strip = styled.div`
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${({ $met }) => ($met ? '#e0e6ea' : '#f3d9a8')};
  background: ${({ $met }) => ($met ? '#f6fafd' : '#fdf4e7')};
`;

const StripHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const StripTitle = styled.span`
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8098a8;
  font-weight: 700;
`;

const StripState = styled.span`
  font-size: 0.75rem;
  font-weight: 800;
  color: ${({ $met }) => ($met ? '#0E9F6E' : '#B06A00')};
`;

const RestoreButton = styled.button`
  margin-left: auto;
  appearance: none;
  background: transparent;
  border: 1px solid #1ba8f1;
  border-radius: 6px;
  color: #0b7fbd;
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 5px 10px;
  cursor: pointer;

  &:hover {
    background: #eaf6fe;
  }
`;

const Checks = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 7px 22px;
`;

const Check = styled.li`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.8rem;
  color: ${({ $met }) => ($met ? '#41545f' : '#0F1B24')};
`;

const Mark = styled.span`
  font-weight: 800;
  font-size: 0.7rem;
  color: ${({ $met }) => ($met ? '#0E9F6E' : '#B06A00')};
`;

const Fix = styled.span`
  display: block;
  font-size: 0.72rem;
  color: #b06a00;
`;

/* Core has nothing to qualify for, so it gets a plain statement rather than an
   empty checklist under a "Not yet earned" heading. */
const PlainStrip = styled.div`
  margin-top: 12px;
  padding: 13px 16px;
  border: 1px solid #e0e6ea;
  border-radius: 12px;
  background: #f6fafd;
  font-size: 0.8rem;
  color: #41545f;

  b {
    color: #0f1b24;
    font-weight: 800;
  }
`;

/* ------------------------------------------------------------------ pieces */

/**
 * The hardware photographs for the current build, left to right, largest first.
 *
 * Integrated pairings collapse to a single tile — showing the FoxESS cabinet
 * twice, or a Tesla inverter beside the Powerwall that contains it, would
 * misrepresent what actually gets mounted on the wall.
 */
function collagePieces(hardware) {
  const pieces = [
    {
      key: 'module',
      image: hardware?.module?.image,
      role: 'Modules',
      name: hardware?.module?.name,
      wide: true,
    },
  ];

  const quantity = Number(hardware?.batteryQuantity) || 0;

  if (isIntegratedInverter(hardware)) {
    pieces.push({
      key: 'integrated',
      image: hardware?.battery?.image,
      role: quantity > 1 ? `Inverter + battery ×${quantity}` : 'Inverter + battery',
      name: hardware?.battery?.name,
    });
  } else {
    pieces.push({
      key: 'inverter',
      image: hardware?.inverter?.image,
      role: 'Inverter',
      name: hardware?.inverter?.name,
    });
    if (quantity > 0) {
      pieces.push({
        key: 'battery',
        image: hardware?.battery?.image,
        role: `Battery ×${quantity}`,
        name: hardware?.battery?.name,
      });
    }
  }

  if (Number(hardware?.serviceQuantity) > 0) {
    pieces.push({
      key: 'service',
      image: hardware?.services?.image,
      role: `EV charger ×${hardware.serviceQuantity}`,
      name: hardware?.services?.name,
    });
  }

  return pieces.filter((piece) => piece.image);
}

/* ------------------------------------------------------------------ export */

const PackagePicker = ({
  hardware,
  selectedPackage,
  onSelectPackage,
  annualProduction,
  subtotal,
  monthlyPayment,
  estimateMonthly,
}) => {
  const active =
    PACKAGES.find((pkg) => pkg.id === selectedPackage) || PACKAGES[0];
  const rules = packageRules(active.id, hardware);
  const allMet = rules.every((rule) => rule.met);

  const promotionAmount = Number(hardware?.promotionAmount) || 0;
  const earned = promotionAmount > 0 ? hardware.promotion : null;
  const total = Number(subtotal) || 0;
  const afterPromotion = Number(hardware?.totalAfterPromotion) || total;

  const systemKw = (Number(hardware?.systemSize) || 0) / 1000;
  const panels = hardware?.module?.watts
    ? Math.round((Number(hardware.systemSize) || 0) / hardware.module.watts)
    : 0;
  const capacity = Number(hardware?.batteryCapacity) || 0;
  const backup = Number(hardware?.backupHours) || 0;

  return (
    <>
      <Hero>
        <Collage>
          {collagePieces(hardware).map((piece) => (
            <Piece key={piece.key} $wide={piece.wide}>
              <Shot>
                <img src={piece.image} alt={piece.name} />
              </Shot>
              <Caption>
                <b>{piece.role}</b>
                {piece.name}
              </Caption>
            </Piece>
          ))}
        </Collage>

        <Panel>
          <Eyebrow>Your package</Eyebrow>
          <PackageName>{active.name}</PackageName>
          <Subtitle>{active.subtitle}</Subtitle>

          <Stats>
            <Stat>
              <StatValue>
                {systemKw.toFixed(2)} <small>kW</small>
              </StatValue>
              <StatKey>{panels} panels</StatKey>
            </Stat>
            <Stat>
              <StatValue>{Math.round(annualProduction || 0).toLocaleString()}</StatValue>
              <StatKey>kWh a year</StatKey>
            </Stat>
            <Stat>
              <StatValue>
                {capacity ? capacity.toFixed(2) : '—'} {capacity ? <small>kWh</small> : null}
              </StatValue>
              <StatKey>
                {capacity ? `${hardware.batteryQuantity} batteries` : 'No storage'}
              </StatKey>
            </Stat>
            <Stat>
              <StatValue>
                {backup ? backup : '—'} {backup ? <small>hr</small> : null}
              </StatValue>
              <StatKey>{backup ? 'Backup power' : 'No backup'}</StatKey>
            </Stat>
          </Stats>

          <PriceRow>
            <PriceNow>{money(afterPromotion)}</PriceNow>
            {promotionAmount > 0 && <PriceWas>{money(total)}</PriceWas>}
            {monthlyPayment > 0 && (
              <Monthly>
                <b>{money(monthlyPayment)}</b>
                {/* Floor, not round: Climate First's term is 30.5 years
                    because the first six months are deferred, and rounding sold
                    it as 31. APR is omitted for a payment-factor loan — Credit
                    Human doesn't quote one, so printing 0% would be a lie. */}
                <span>
                  a month
                  {hardware?.selectedProduct?.term
                    ? ` · ${Math.floor(hardware.selectedProduct.term)} yr`
                    : ''}
                  {!hardware?.selectedProduct?.factor && hardware?.selectedProduct?.apr
                    ? ` · ${hardware.selectedProduct.apr}% APR`
                    : ''}
                  {hardware?.selectedLender ? ` · ${hardware.selectedLender}` : ''}
                </span>
              </Monthly>
            )}
          </PriceRow>

          <PromoFlag $earned={Boolean(earned)}>
            {earned
              ? `${earned.headline} · ${earned.label}`
              : 'Standard pricing — no promotion on this build'}
          </PromoFlag>
        </Panel>
      </Hero>

      <Switcher>
        {PACKAGES.map((pkg) => {
          const preview = estimateMonthly ? estimateMonthly(pkg) : null;
          return (
            <SwitchButton
              key={pkg.id}
              type="button"
              $active={pkg.id === active.id}
              aria-pressed={pkg.id === active.id}
              onClick={() => onSelectPackage(pkg)}
            >
              <SwitchPromo $none={!pkg.promotion}>
                {pkg.promotion ? pkg.promotion.headline : 'Standard pricing'}
              </SwitchPromo>
              <SwitchName>{pkg.name}</SwitchName>
              <SwitchSubtitle>{pkg.subtitle}</SwitchSubtitle>
              {preview && (
                <SwitchFigure>
                  <b>{money(preview.total)}</b>
                  {preview.monthly > 0 ? ` · ${money(preview.monthly)}/mo` : ''}
                </SwitchFigure>
              )}
            </SwitchButton>
          );
        })}
      </Switcher>

      {active.promotion ? (
        <Strip $met={allMet}>
          <StripHead>
            <StripTitle>{active.name} promotion</StripTitle>
            <StripState $met={allMet}>
              {allMet
                ? earned && earned.id !== active.id
                  ? `Earned — this build takes the ${earned.name} promotion instead`
                  : 'Earned'
                : 'Not yet earned'}
            </StripState>
            {!allMet && (
              <RestoreButton type="button" onClick={() => onSelectPackage(active)}>
                Restore the package
              </RestoreButton>
            )}
          </StripHead>
          <Checks>
            {rules.map((rule) => (
              <Check key={rule.label} $met={rule.met}>
                <Mark $met={rule.met}>{rule.met ? '✓' : '×'}</Mark>
                <span>
                  {rule.label}
                  {!rule.met && <Fix>{rule.fix}</Fix>}
                </span>
              </Check>
            ))}
          </Checks>
        </Strip>
      ) : (
        <PlainStrip>
          <b>{active.name}</b> is priced as standard — there's no promotion attached to it. The
          other packages each carry one.
        </PlainStrip>
      )}
    </>
  );
};

export default PackagePicker;
